import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'app/components/ui/card';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import { useInventorySlice } from '../slice';
import { ProductInventoryDetails as ProductInventoryDetailsType } from 'types/inventory';
import { Loader2, Package, TrendingUp, TrendingDown, Calendar, User, FileText } from 'lucide-react';

interface ProductInventoryDetailsProps {
    productId: number;
    onClose: () => void;
}

const ProductInventoryDetails: React.FC<ProductInventoryDetailsProps> = ({
    productId,
    onClose,
}) => {
    const { useGetProductInventoryDetailsQuery } = useInventorySlice();
    const { data: details, isLoading, error } = useGetProductInventoryDetailsQuery(productId);

    if (isLoading) {
        return (
            <Dialog open onOpenChange={onClose}>
                <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                    <div className="flex items-center justify-center h-32">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open onOpenChange={onClose}>
                <DialogContent className="sm:max-w-md">
                    <Alert variant="destructive">
                        <AlertDescription>
                            Failed to load product inventory details. Please try again later.
                        </AlertDescription>
                    </Alert>
                </DialogContent>
            </Dialog>
        );
    }

    const product = details?.product;
    const history = details?.inventory_history || [];
    const stats = details?.statistics;

    const getActionTypeColor = (actionType: string) => {
        switch (actionType) {
            case 'adjustment':
                return 'bg-blue-100 text-blue-800';
            case 'sale':
                return 'bg-green-100 text-green-800';
            case 'return':
                return 'bg-purple-100 text-purple-800';
            case 'damage':
                return 'bg-red-100 text-red-800';
            case 'transfer':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getActionTypeIcon = (actionType: string) => {
        switch (actionType) {
            case 'adjustment':
                return <Package className="h-4 w-4" />;
            case 'sale':
                return <TrendingDown className="h-4 w-4" />;
            case 'return':
                return <TrendingUp className="h-4 w-4" />;
            case 'damage':
                return <Package className="h-4 w-4" />;
            case 'transfer':
                return <Package className="h-4 w-4" />;
            default:
                return <Package className="h-4 w-4" />;
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Product Inventory Details
                    </DialogTitle>
                    <DialogDescription>
                        Detailed inventory information for {product?.product_name}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Product Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Product Information</CardTitle>
                            <CardDescription>
                                Current product details and stock status
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Product Name:</span>
                                        <span className="text-sm">{product?.product_name}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">SKU:</span>
                                        <span className="text-sm">{product?.sku}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Supplier:</span>
                                        <span className="text-sm">{product?.supplier}</span>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Current Stock:</span>
                                        <Badge variant={product?.stock_quantity === 0 ? "destructive" : product?.stock_quantity < 10 ? "secondary" : "default"}>
                                            {product?.stock_quantity} units
                                        </Badge>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Sale Price:</span>
                                        <span className="text-sm font-medium">${product?.sale_price}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium">Status:</span>
                                        <Badge variant={product?.status === 1 ? "default" : "secondary"}>
                                            {product?.status === 1 ? "Active" : "Inactive"}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Statistics */}
                    {stats && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Inventory Statistics</CardTitle>
                                <CardDescription>
                                    Summary of inventory movements and trends
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold">{stats.total_movements}</div>
                                        <p className="text-xs text-muted-foreground">Total Movements</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">{stats.total_additions}</div>
                                        <p className="text-xs text-muted-foreground">Total Additions</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-red-600">{stats.total_reductions}</div>
                                        <p className="text-xs text-muted-foreground">Total Reductions</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold">{stats.avg_addition}</div>
                                        <p className="text-xs text-muted-foreground">Avg Addition</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Inventory History */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Inventory History</CardTitle>
                            <CardDescription>
                                Complete history of inventory movements
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {history.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Action</TableHead>
                                            <TableHead>Change</TableHead>
                                            <TableHead>Stock</TableHead>
                                            <TableHead>Cost</TableHead>
                                            <TableHead>Reference</TableHead>
                                            <TableHead>Notes</TableHead>
                                            <TableHead>User</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {history.map((movement) => (
                                            <TableRow key={movement.log_id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm">
                                                            {new Date(movement.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={getActionTypeColor(movement.action_type)}>
                                                        <div className="flex items-center gap-1">
                                                            {getActionTypeIcon(movement.action_type)}
                                                            <span>{movement.action_type}</span>
                                                        </div>
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={movement.quantity_change > 0 ? "default" : "secondary"}>
                                                        {movement.quantity_change > 0 ? '+' : ''}{movement.quantity_change}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-sm">{movement.previous_stock}</span>
                                                        <span className="text-muted-foreground">→</span>
                                                        <span className="text-sm font-medium">{movement.new_stock}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {movement.unit_cost ? (
                                                        <span className="text-sm">${movement.unit_cost}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {movement.reference_number ? (
                                                        <span className="text-sm">{movement.reference_number}</span>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {movement.notes ? (
                                                        <div className="flex items-center gap-1">
                                                            <FileText className="h-4 w-4 text-muted-foreground" />
                                                            <span className="text-sm max-w-32 truncate">{movement.notes}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {movement.performed_by ? (
                                                        <div className="flex items-center gap-1">
                                                            <User className="h-4 w-4 text-muted-foreground" />
                                                            <span className="text-sm">{movement.performed_by}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="text-center py-8">
                                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-muted-foreground">No inventory history available</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex justify-end">
                        <Button onClick={onClose}>
                            Close
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProductInventoryDetails;
